import React, { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { Modal } from "reactstrap";
import { Icon } from "../../../components/Component";
import { serviceGetLivechatAttachment } from "../../../services/livechat";
import { pollLivechatAttachment, rejectMessage } from "./livechatUpload";

export const MEDIA_TYPES = new Set(["image", "video", "file"]);

// an optimistic copy has no stored attachment yet: LivechatUploading shows it
export const hasMedia = (message) =>
  MEDIA_TYPES.has(message?.type) && Boolean(message.attachment) && !message.deleted_at && !message.local;

export const formatSize = (bytes = 0) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Live Chat links expire after 1 hour; a fresh one is shared by the bubble and
// the gallery for 50 minutes so a page of expired images stays under the rate limit
const LINK_TTL_MS = 50 * 60 * 1000;
const freshLinks = new Map();

export const fetchFreshLink = (token, conversationId, seq) => {
  const key = `${conversationId}:${seq}`;
  const cached = freshLinks.get(key);

  if (cached && Date.now() - cached.at < LINK_TTL_MS) return cached.promise;

  const promise = serviceGetLivechatAttachment(token, conversationId, seq).then((res) => {
    if (res?.status === 200) return res.data.url;
    freshLinks.delete(key);
    return null;
  });
  freshLinks.set(key, { at: Date.now(), promise });

  return promise;
};

// Live Chat links expire after 1 hour: ask the CRM for a fresh one when needed
const useFreshLink = (conversationId, message) => {
  const [cookies] = useCookies(["SIDikey"]);
  const [url, setUrl] = useState(message.attachment?.url);
  const [isGone, setIsGone] = useState(false);
  const [hasRetried, setHasRetried] = useState(false);
  const incomingUrl = message.attachment?.url;

  // a file that just passed validation arrives with its url
  useEffect(() => {
    if (incomingUrl) setUrl(incomingUrl);
  }, [incomingUrl]);

  const refresh = async () => {
    const fresh = await fetchFreshLink(cookies.SIDikey, conversationId, message.seq);

    if (!fresh) {
      setIsGone(true);
      return null;
    }

    setUrl(fresh);
    return fresh;
  };

  // an expired <img>/<video> retries once with a fresh link
  const onMediaError = () => {
    if (hasRetried) {
      setIsGone(true);
      return;
    }

    setHasRetried(true);
    refresh();
  };

  return { url, isGone, refresh, onMediaError };
};

// opens the tab before the request so the browser does not block the popup
const openInNewTab = async (refresh) => {
  const tab = window.open("", "_blank");
  const fresh = await refresh();

  if (!tab) return;
  if (!fresh) {
    tab.close();
    return;
  }

  tab.opener = null;
  tab.location.href = fresh;
};

export const LivechatImageViewer = ({ image, onClose }) => {
  const [isZoomed, setIsZoomed] = useState(false);

  return (
    <Modal isOpen={Boolean(image)} toggle={onClose} size="xl" centered onClosed={() => setIsZoomed(false)}>
      <div className="d-flex justify-content-between align-items-center p-2">
        <span className="sub-text text-truncate">{image?.filename}</span>
        <button type="button" className="btn btn-icon btn-trigger" aria-label="Tutup" onClick={onClose}>
          <Icon name="cross" />
        </button>
      </div>
      <div className="text-center p-2" style={{ maxHeight: "80vh", overflow: "auto" }}>
        {image && (
          <img
            src={image.url}
            alt={image.filename}
            title={isZoomed ? "Klik untuk perkecil" : "Klik untuk perbesar"}
            style={
              isZoomed
                ? { maxWidth: "none", cursor: "zoom-out" }
                : { maxWidth: "100%", maxHeight: "75vh", cursor: "zoom-in" }
            }
            onClick={() => setIsZoomed((zoomed) => !zoomed)}
          />
        )}
      </div>
    </Modal>
  );
};

const Unavailable = () => <span className="lc-unsent">Lampiran tidak tersedia</span>;

const FileCard = ({ attachment, onOpen }) => (
  <button type="button" className="lc-file" onClick={onOpen}>
    <Icon name="file-pdf" className="lc-file-icon" />
    <span className="text-left text-truncate">
      <span className="d-block text-truncate">{attachment.filename}</span>
      <span className="lc-time">{formatSize(attachment.size)}</span>
    </span>
  </button>
);

const FILE_ICONS = { image: "img", video: "video-fill", file: "file-pdf" };

// The agent's file while it is compressed and uploaded, before the message exists
export const LivechatUploading = ({ message }) => (
  <div className="lc-file">
    <Icon name={FILE_ICONS[message.type] ?? "file"} className="lc-file-icon" />
    <span className="text-left text-truncate">
      <span className="d-block text-truncate">{message.attachment.filename}</span>
      <span className="lc-time">{formatSize(message.attachment.size)}</span>
    </span>
  </div>
);

// Live Chat validates the uploaded bytes; the visitor only sees a ready file
const useValidation = (conversationId, attachment, onReady) => {
  const [cookies] = useCookies(["SIDikey"]);
  const [checked, setChecked] = useState(null);
  const isValidating = attachment?.status === "validating" && !checked;

  useEffect(() => {
    if (!isValidating) return undefined;

    let isCancelled = false;
    pollLivechatAttachment(cookies.SIDikey, conversationId, attachment.id, () => isCancelled).then((result) => {
      if (isCancelled || !result) return;
      setChecked(result);
      if (result.status === "ready") onReady();
    });

    return () => {
      isCancelled = true;
    };
    // poll once per attachment
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidating, conversationId, attachment?.id, cookies.SIDikey]);

  return { status: checked?.status ?? attachment?.status, rejectReason: checked?.rejectReason ?? attachment?.reject_reason };
};

// Bubble content for an image, video or PDF message
export const LivechatAttachment = ({ conversationId, message, onOpenImage }) => {
  const { url, isGone, refresh, onMediaError } = useFreshLink(conversationId, message);
  const { attachment } = message;
  const { status, rejectReason } = useValidation(conversationId, attachment, () => {
    if (message.type !== "file") refresh();
  });

  if (status === "validating") return <span className="lc-time">Memeriksa file…</span>;
  if (status === "rejected") return <span className="lc-failed">Ditolak: {rejectMessage(rejectReason)}</span>;
  if (isGone) return <Unavailable />;

  // a PDF fetches its link on click; images and videos need one to render
  if (message.type === "file") {
    return <FileCard attachment={attachment} onOpen={() => openInNewTab(refresh)} />;
  }

  if (!url) return <Unavailable />;

  if (message.type === "image") {
    return (
      <img
        src={url}
        alt={attachment.filename}
        className="lc-media"
        onError={onMediaError}
        onClick={() => onOpenImage({ url, filename: attachment.filename })}
      />
    );
  }

  return <video src={url} className="lc-media" controls preload="metadata" onError={onMediaError} />;
};

const GalleryItem = ({ conversationId, message, onOpenImage }) => {
  const { url, isGone, refresh, onMediaError } = useFreshLink(conversationId, message);
  const { attachment } = message;

  if (isGone) return null;

  if (message.type === "image") {
    return (
      <li>
        <img
          className="sidebar-image"
          src={url}
          alt={attachment.filename}
          onError={onMediaError}
          onClick={() => onOpenImage({ url, filename: attachment.filename })}
        />
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        className="sidebar-document border-0"
        title={attachment.filename}
        onClick={() => openInNewTab(refresh)}
      >
        <div className="d-flex justify-content-center align-items-center h-100">
          <Icon name={message.type === "video" ? "video-fill" : "file-pdf"} className="sidebar-document-title" />
        </div>
      </button>
    </li>
  );
};

// Shared media of the loaded messages, newest first
export const LivechatMediaGallery = ({ conversationId, messages }) => {
  const [image, setImage] = useState(null);
  const media = messages.filter(hasMedia).reverse();

  if (media.length === 0) return <span className="sub-text">Belum ada media</span>;

  return (
    <>
      <ul className="chat-profile-media row">
        {media.map((message) => (
          <GalleryItem key={message.id} conversationId={conversationId} message={message} onOpenImage={setImage} />
        ))}
      </ul>
      <LivechatImageViewer image={image} onClose={() => setImage(null)} />
    </>
  );
};
